import { MessageRole } from "models/enumerations/conversations/message-role";
import ConversationApplicationContextRecord from "models/view-models/conversations/conversation-application-context-record";
import ConversationMessageRecord from "models/view-models/conversations/conversation-message-record";
import { ConversationMessageStatus } from "models/view-models/conversations/conversation-message-status";
import ConversationRecord from "models/view-models/conversations/conversation-record";
import { upsertMessageParts } from "models/view-models/conversations/message-part-factory";
import { Dispatch } from "react";
import { Thunk } from "utilities/atoms/atom-with-thunks-reducer";
import {
    CurrentConversationState,
    ConversationsAction,
    ConversationStateStatus,
} from "utilities/atoms/conversations/current-conversation/conversation-atom.reducer";
import { startConversation } from "utilities/atoms/conversations/current-conversation/start-conversation.thunk";
import ConversationService from "utilities/services/conversations/conversation-service";

interface SendMessageParams {
    prompt: string;
    messageId?: string;
    refreshConversationHistory?: () => void;
    applicationContext?: ConversationApplicationContextRecord;
}

/**
 * Sends a message in the current conversation.
 * This thunk will first ensure that a conversation is started.
 */
export const sendMessage =
    ({
        prompt,
        messageId,
        refreshConversationHistory,
        applicationContext,
    }: SendMessageParams): Thunk<
        CurrentConversationState,
        ConversationsAction
    > =>
    async (dispatch, getState) => {
        const abortController = new AbortController();
        const signal = abortController.signal;

        const userMessage = findOrCreateUserMessage(
            messageId,
            prompt,
            getState
        );

        const conversation = await startOrContinueConversation(
            dispatch,
            getState,
            userMessage
        );

        try {
            const stream = ConversationService.createOrContinue(
                {
                    conversationId: conversation?.id,
                    parentId: userMessage.parentId,
                    messages: [
                        {
                            role: MessageRole.User,
                            content: prompt,
                        },
                    ],
                    applicationContext,
                },
                signal
            );

            const reader = stream.getReader();

            const debouncedDispatch = createDebouncedDispatcher(dispatch);

            while (true) {
                const { done, value } = await reader.read();
                if (done) {
                    dispatch({ type: "CONVERSATION_READY" });

                    if (conversation?.id == null) {
                        refreshConversationHistory?.();
                    }

                    break;
                }

                const { result, resultObject } = value;
                if (result?.doesNotHaveErrors() && resultObject) {
                    if (resultObject.role === MessageRole.System) {
                        continue;
                    }
                    if (resultObject.role === MessageRole.User) {
                        dispatch({
                            type: "CONVERSATION_READY",
                            id: resultObject.conversationId,
                            title: prompt,
                        });
                        dispatch({
                            type: "UPDATE_CONVERSATION_STATUS",
                            status: ConversationStateStatus.StreamingMessage,
                            streamingResponseAbortController: abortController,
                        });
                    }

                    debouncedDispatch(
                        resultObject.with({
                            status: ConversationMessageStatus.Streaming,
                        })
                    );

                    continue;
                }

                // If we reach here, it means there was an error
                dispatch({
                    type: "ERROR",
                    error: new Error(
                        (
                            result?.listErrorMessages() ?? [
                                "Failed to send message",
                            ]
                        ).join(", ")
                    ),
                });
                break;
            }
        } catch (error) {
            if (signal.aborted) {
                return;
            }

            dispatch({
                type: "ERROR",
                error: new Error(`${error}`),
            });
        }
    };

/**
 * Starts a new conversation or continues an existing one and adds a user message.
 */
async function startOrContinueConversation(
    dispatch: Dispatch<ConversationsAction>,
    getState: () => CurrentConversationState,
    userMessage: ConversationMessageRecord
): Promise<ConversationRecord | undefined> {
    const { currentConversation } = getState();

    if (currentConversation?.id) {
        dispatch({
            type: "UPDATE_MESSAGE",
            message: userMessage,
        });
        return currentConversation;
    }

    await startConversation(userMessage)(dispatch);

    return getState().currentConversation;
}

/**
 * Gets the last message in the conversation with the specified role.
 * If there are no messages with the specified role, it returns undefined.
 */
function getLastMessageByRole(
    conversation: ConversationRecord | undefined,
    role: MessageRole
): ConversationMessageRecord | undefined {
    if (conversation == null) {
        return undefined;
    }

    const [lastMessage] = conversation.messages
        .filter((message) => message.role === role)
        .sort((a, b) => (b.index ?? 0) - (a.index ?? 0));

    return lastMessage;
}

/**
 * Finds an existing user message by ID and updates its content or creates a new one if not found.
 */
function findOrCreateUserMessage(
    messageId: string | undefined,
    prompt: string,
    getState: () => CurrentConversationState
): ConversationMessageRecord {
    const { currentConversation } = getState();

    if (currentConversation?.messages && messageId) {
        const userMessage = currentConversation.messages.find(
            (m) => m.id === messageId
        );
        if (userMessage) {
            return userMessage.with({ content: prompt });
        }
    }

    return new ConversationMessageRecord({
        content: prompt,
        prompt: prompt,
        role: MessageRole.User,
        parentId: getLastMessageByRole(
            currentConversation,
            MessageRole.Assistant
        )?.id,
    });
}

const hasIdentityChanged = (
    a: ConversationMessageRecord | null,
    b: ConversationMessageRecord
): boolean => {
    if (!a) return false;

    if (a.id !== b.id) return true;

    if (a.parentId != null && b.parentId != null && a.parentId !== b.parentId) {
        return true;
    }

    if (
        typeof a.index === "number" &&
        typeof b.index === "number" &&
        a.index !== b.index
    ) {
        return true;
    }

    return false;
};

function createDebouncedDispatcher(dispatch: Dispatch<ConversationsAction>) {
    let timer: NodeJS.Timeout | null = null;
    let accumulatedMessage: ConversationMessageRecord | null = null;
    const delayInMs = 100;

    const dispatchAccumulated = () => {
        if (accumulatedMessage) {
            dispatch({
                type: "UPDATE_MESSAGE",
                message: accumulatedMessage,
            });
            accumulatedMessage = null;
        }
        timer = null;
    };

    return (message: ConversationMessageRecord) => {
        if (hasIdentityChanged(accumulatedMessage, message)) {
            dispatchAccumulated();
        }

        if (!accumulatedMessage) {
            accumulatedMessage = message;
        } else {
            accumulatedMessage = accumulatedMessage.with({
                content:
                    (accumulatedMessage.content ?? "") +
                    (message.content ?? ""),
                citations: [
                    ...accumulatedMessage.citations,
                    ...message.citations,
                ],
                parts: upsertMessageParts(
                    accumulatedMessage.parts,
                    message.parts
                ),
                id: message.id ?? accumulatedMessage.id,
                status: message.status ?? accumulatedMessage.status,
                chatFinishReason:
                    message.chatFinishReason ??
                    accumulatedMessage.chatFinishReason,
                createdOn: message.createdOn ?? accumulatedMessage.createdOn,
                index: message.index ?? accumulatedMessage.index,
                parentId: message.parentId ?? accumulatedMessage.parentId,
                conversationId:
                    message.conversationId ?? accumulatedMessage.conversationId,
                role: message.role ?? accumulatedMessage.role,
                prompt: message.prompt ?? accumulatedMessage.prompt,
                feedback: message.feedback ?? accumulatedMessage.feedback,
            });
        }

        if (timer) clearTimeout(timer);
        timer = setTimeout(dispatchAccumulated, delayInMs);
    };
}
